import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/Input';
import Button from '../../../common/components/Button';
import Stack from '../../../common/components/primitives/Stack';
import Cluster from '../../../common/components/primitives/Cluster';
import Text from '../../../common/components/base/Text';
import FormFieldWrapper from '../../../components/forms/FormFieldWrapper';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from '../../../utils/notify';
import {
  apiAdminUpdateOrganisationMessageTemplate,
  apiAdminResetOrganisationMessageTemplate,
} from '../../../common/api/admin';
import { MESSAGE_TEMPLATE_VARIABLES } from '../../../common/constants';
import { purposeLabel, sendsSms } from '../sections';
import { EMPTY_EMAIL, buildTemplateSubmission } from '../submission';
import { authoredLanguages } from '../completeness';
import { shortLinkLength, smsSegments } from '../smsSegments';
import settings from '../../../common/settings';

/**
 * TECH-2730. The message editor, inline in the card rather than in a modal.
 *
 * WHAT CHANGES, AND WHY EACH ONE.
 *
 * NO MODAL. Everything else on this surface is edited in place; the modal was
 * the exception and it hid the rest of the message while you worked on one
 * language of it.
 *
 * THE BASE-LANGUAGE SELECT IS GONE. This is the fix for finding F2, and it is
 * a deletion rather than a relabel. Two controls both read
 * t('forms:language.label') — the record's base language and the
 * content-editor language — one rendering a code and one a name, which reads
 * as a formatting inconsistency rather than a distinction. They do materially
 * different things: the base language is the fallback for EVERY patient whose
 * own language has not been authored, so changing it silently changes what a
 * group of patients receives, with no error and no warning. It caught the
 * tester twice in one session, the second time after the trap was documented.
 *
 * The base language is now shown pinned and locked as the DEFAULT row, and
 * changing it is a deliberate, confirmed action. `language` stays in
 * component state regardless: both API schemas require it and the save path
 * reads it.
 *
 * PURPOSE IS READ-ONLY TEXT, not a disabled Select. Re-pointing a row at
 * another purpose leaves that purpose with two rows once the next Resync
 * re-creates the vacated one, and the resolvers take the first match, so the
 * delivered content becomes arbitrary. A disabled control invites the
 * question; text does not.
 *
 * NO CREATE PATH. It was unreachable: `isDefault` is never set now that the
 * publication no longer synthesises default rows, so `isUpdate` was always
 * true. Carrying the branch forward would have carried a dead permission
 * check and a dead dialog title with it.
 */

/**
 * One chip shape for the whole editor.
 *
 * The brand's chip is a wash tint with coloured TEXT at 3px radius — not a
 * filled block. Every state here was rendering as a solid pill (blue Default,
 * green Translated, amber Not set, blue variable pills), which put the loudest
 * colour on the page onto its least important information and pushed well past
 * the ~5% signal share the palette allows.
 *
 * `neutral` is deliberate for "not set": an unwritten translation is an absence,
 * not a clinical status, and amber is reserved for something needing review.
 */
const CHIP_TONES = {
  neutral: 'border-divider text-on-surface opacity-70',
  info: 'border-primary text-primary',
  success: 'border-success text-success',
  // The `-text` variant, never the DEFAULT warning token as text: that one is
  // a fill colour, 1.25:1 on white — QA could not read "Not configured" (RD10).
  warning: 'border-warning text-warning-text',
};

const Chip = ({ tone, children }) => (
  <span
    className={[
      'inline-flex shrink-0 items-center rounded-sm border px-2 py-0.5',
      'font-mono text-xs tracking-wider',
      CHIP_TONES[tone] || CHIP_TONES.neutral,
    ].join(' ')}
  >
    {children}
  </span>
);

Chip.propTypes = {
  tone: PropTypes.oneOf(Object.keys(CHIP_TONES)),
  children: PropTypes.node.isRequired,
};

Chip.defaultProps = { tone: 'neutral' };

const MessageTemplateEditor = ({
  template,
  configuredLanguages,
  canUpdate,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSaving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en');
  const [emailByLanguage, setEmailByLanguage] = useState({});
  const [openLanguage, setOpenLanguage] = useState(null);
  const [pendingDefault, setPendingDefault] = useState(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const templateId = template && template._id;
  const purpose = (template && template.purpose) || '';
  const contentIsSent = sendsSms(purpose);

  /**
   * Hydrate ONCE per row, not on every change to the underlying document.
   *
   * `template` is a live DDP document. Depending on it directly meant any
   * store update while the editor was open — a concurrent edit by another
   * administrator, or simply a re-delivery of the subscription — overwrote
   * every field and discarded unsaved work with no warning. Keyed on the row
   * id alone: an earlier version folded the loaded/empty state into the key,
   * so a websocket reconnect that briefly dropped the document re-hydrated
   * and wiped the form.
   */
  const hydratedFor = useRef(null);
  useEffect(() => {
    if (!templateId || hydratedFor.current === templateId) {
      return;
    }
    hydratedFor.current = templateId;
    setName(template.name || '');
    setLanguage(template.language || 'en');
    const byLanguage = {};
    if (template.language) {
      byLanguage[template.language] = {
        ...EMPTY_EMAIL,
        ...(template.template || {}),
      };
    }
    (template.translations || []).forEach((translation) => {
      if (translation && translation.language) {
        byLanguage[translation.language] = {
          ...EMPTY_EMAIL,
          ...(translation.email || {}),
        };
      }
    });
    setEmailByLanguage(byLanguage);
    setOpenLanguage(null);
  }, [templateId, template]);

  const variables = MESSAGE_TEMPLATE_VARIABLES[purpose] || [];
  const authored = useMemo(
    () => new Set(authoredLanguages(template)),
    [template],
  );

  /**
   * Rows follow the ORGANISATION's configured set, with the base language
   * pinned first. A language that has content but is no longer configured is
   * still shown, at the end — hiding it would strand wording an author cannot
   * see, reach, or delete.
   */
  const languageRows = useMemo(() => {
    const configured = configuredLanguages || [];
    const ordered = [language, ...configured.filter((lng) => lng !== language)];
    const extra = Object.keys(emailByLanguage).filter(
      (lng) => !ordered.includes(lng),
    );
    return [...ordered, ...extra].map((lng) => ({
      language: lng,
      isDefault: lng === language,
      isConfigured: lng === language || configured.includes(lng),
      hasContent: !!emailByLanguage[lng],
    }));
  }, [language, configuredLanguages, emailByLanguage]);

  const handleFieldChange = useCallback(
    (lng, field) => (event) => {
      const { value } = event.target;
      setEmailByLanguage((previous) => ({
        ...previous,
        [lng]: { ...EMPTY_EMAIL, ...previous[lng], [field]: value },
      }));
    },
    [],
  );

  const handleAddLanguage = useCallback((lng) => {
    setEmailByLanguage((previous) => ({
      ...previous,
      [lng]: { ...EMPTY_EMAIL, ...previous[lng] },
    }));
    setOpenLanguage(lng);
  }, []);

  const handleRemoveLanguage = useCallback((lng) => {
    setEmailByLanguage((previous) => {
      const next = { ...previous };
      delete next[lng];
      return next;
    });
    setOpenLanguage((current) => (current === lng ? null : current));
  }, []);

  const handleConfirmDefault = useCallback(() => {
    setLanguage(pendingDefault);
    setPendingDefault(null);
  }, [pendingDefault]);

  const handleReset = useCallback(() => {
    setSaving(true);
    dispatch(
      callMethod(apiAdminResetOrganisationMessageTemplate, { templateId }),
    )
      .then(() => {
        notifySuccess(t('confirmations:saveEmailTemplate.success'))();
        setConfirmingReset(false);
        // Force re-hydration from the restored row.
        hydratedFor.current = null;
        onClose();
        return null;
      })
      .catch(notifyError())
      .finally(() => setSaving(false));
  }, [dispatch, templateId, t, onClose]);

  const handleSubmit = useCallback(() => {
    const { error, baseEmail, translations, languagesMissingContent } =
      buildTemplateSubmission({ language, emailByLanguage, purpose });

    if (error) {
      notifyError()(new Error(t(error.key, error.params)));
      return;
    }

    setSaving(true);
    dispatch(
      callMethod(apiAdminUpdateOrganisationMessageTemplate, {
        templateId,
        name,
        language,
        template: baseEmail,
        translations,
      }),
    )
      .then(() => {
        // WARN, do not block. Raised and rejected twice in review: a complete
        // translation has no problem, and this only concerns one saved with
        // the SMS field blank, which yields a translated email and an English
        // SMS. The warning IS the safeguard on that point.
        //
        // FINDING F1, and the cause is not what it looked like. notifyWarning
        // already passes duration: 0, so the warning does persist — but
        // notifySuccess inherits the global 2.5s duration and its onClose
        // handler is `() => notification.destroy()`, which destroys EVERY
        // notification, not just itself. So the warning was wiped 2.5 seconds
        // after appearing, by the success toast closing. That is the "four
        // attempts to read it" in the QA report.
        //
        // Showing one or the other rather than both fixes it here without
        // changing shared notification behaviour: the destroy() is load-
        // bearing for getContainer, per its own comment, and changing it
        // would affect every screen. The warning subsumes the success message
        // anyway — it only appears when the save succeeded.
        if (languagesMissingContent.length > 0) {
          // notifyWarning is NOT curried and takes an OBJECT — unlike
          // notifyError and notifySuccess, which are. Calling it with a
          // string and invoking the result destructured `message` to
          // undefined (blank warning) and then threw TypeError on the
          // returned key, inside the .then and BEFORE onClose — so a
          // SUCCESSFUL save reported failure and left the editor open, on
          // exactly the path finding F1 exists to fix. Every other caller in
          // this app uses the object form; so did the dialog this replaced.
          notifyWarning({
            message: t('translationMissingContentTitle'),
            description: t('translationMissingContent', {
              languages: languagesMissingContent.join(', '),
            }),
          });
        } else {
          notifySuccess(t('confirmations:saveEmailTemplate.success'))();
        }
        onClose();
        return null;
      })
      .catch(notifyError())
      .finally(() => setSaving(false));
  }, [
    dispatch,
    templateId,
    name,
    language,
    emailByLanguage,
    purpose,
    t,
    onClose,
  ]);

  if (!template) {
    return null;
  }

  const isReadOnly = !canUpdate;

  return (
    <div
      data-testid={`message-template-editor-${purpose}`}
      className="stack-4 border border-divider border-t-0 rounded-b-md bg-surface p-4"
    >
      <FormFieldWrapper label={t('name')} required>
        <Input
          data-testid="form-field-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={isReadOnly}
        />
      </FormFieldWrapper>

      <Cluster justify="space-between" wrap="nowrap">
        <Stack space={1}>
          <span className="font-mono text-xs uppercase tracking-wider opacity-60">
            {t('purpose')}
          </span>
          {/* Read-only text, not a disabled control: a disabled Select invites
              the question of how to change it, and the answer is that it must
              not be changed. */}
          <Text>{`${purposeLabel(t, purpose)} (${purpose})`}</Text>
        </Stack>
        {!isReadOnly && (
          <Button
            data-testid="reset-template"
            type="link"
            size="small"
            onClick={() => setConfirmingReset(true)}
          >
            {t('resetEmailTemplate', {
              defaultValue: 'Reset to shipped default',
            })}
          </Button>
        )}
      </Cluster>

      {/* The editor refused an unknown placeholder but never said which are
          valid, so an author discovered the list by being rejected. */}
      {/* Stack carries the test id: Text destructures only
          size/importance/align/token/children and silently drops everything
          else, data-testid included. */}
      {variables.length > 0 && (
        <Stack
          data-testid="template-variables"
          space={1}
          className="border-l-2 border-primary bg-surface p-3 rounded-r-md"
        >
          <Text size="small" importance="low">
            {t('emailTemplateVariables', {
              defaultValue: 'Available variables',
            })}
          </Text>
          {/* Rendered as their own elements rather than interpolated into the
              label: an author can read them at a glance, and the list does not
              silently collapse to a raw "{{variables}}" placeholder wherever
              interpolation is unavailable. */}
          <Cluster space={1}>
            {variables.map((variableName) => (
              <Chip key={variableName} tone="info">
                {`{{${variableName}}}`}
              </Chip>
            ))}
          </Cluster>
        </Stack>
      )}

      {confirmingReset && (
        <Stack
          data-testid="reset-confirmation"
          className="border border-danger bg-danger-muted rounded-md p-4"
        >
          <Text>
            {t('resetEmailTemplateConfirm', {
              defaultValue:
                'This discards this organisation’s wording and every translation on this message. There is no undo.',
            })}
          </Text>
          <Cluster>
            <Button onClick={() => setConfirmingReset(false)}>
              {t('cancel')}
            </Button>
            <Button type="danger" onClick={handleReset} disabled={isSaving}>
              {t('resetEmailTemplateConfirmOk', { defaultValue: 'Reset it' })}
            </Button>
          </Cluster>
        </Stack>
      )}

      {pendingDefault && (
        <Stack
          data-testid="default-language-confirmation"
          className="border border-warning bg-warning-muted rounded-md p-4"
        >
          <Text>
            {t('changeDefaultLanguageConfirm', {
              defaultValue:
                'The default is what a patient receives when none of their own languages is written. Changing it changes what those patients already get.',
            })}
          </Text>
          <Cluster>
            <Button onClick={() => setPendingDefault(null)}>
              {t('cancel')}
            </Button>
            <Button type="primary" onClick={handleConfirmDefault}>
              {t('changeDefaultLanguageConfirmOk', {
                defaultValue: 'Make it the default',
              })}
            </Button>
          </Cluster>
        </Stack>
      )}

      <Stack data-testid="language-rows">
        {languageRows.map((row) => {
          const email = emailByLanguage[row.language] || EMPTY_EMAIL;
          const isOpen = openLanguage === row.language;
          return (
            <div
              key={row.language}
              className={[
                'border rounded-md p-3',
                isOpen
                  ? 'border-primary bg-surface'
                  : 'border-divider bg-surface',
              ].join(' ')}
            >
              <Cluster justify="space-between" wrap="nowrap">
                <Cluster wrap="nowrap">
                  <span
                    data-testid={`language-row-${row.language}`}
                    className="font-mono text-sm uppercase tracking-wider"
                  >
                    {row.language.toUpperCase()}
                  </span>
                  {row.isDefault && (
                    <Chip tone="info">
                      {t('defaultLanguage', { defaultValue: 'Default' })}
                    </Chip>
                  )}
                  {!row.isConfigured && (
                    <Chip tone="warning">
                      {t('languageNotConfigured', {
                        defaultValue: 'Not configured',
                      })}
                    </Chip>
                  )}
                  {authored.has(row.language) ? (
                    <Chip tone="success">
                      {t('translated', { defaultValue: 'Translated' })}
                    </Chip>
                  ) : (
                    <Chip>{t('notSet', { defaultValue: 'Not set' })}</Chip>
                  )}
                </Cluster>
                <Cluster wrap="nowrap">
                  {/* The ONLY way to open a language. The code used to toggle
                      the row, but in QA nobody found it: it reads as a label,
                      not a control. Shown read-only too, or a view-only admin
                      could no longer read the wording; the fields stay
                      disabled for them. */}
                  {row.hasContent && (
                    <Button
                      data-testid={`edit-language-${row.language}`}
                      size="small"
                      onClick={() =>
                        setOpenLanguage(isOpen ? null : row.language)
                      }
                    >
                      {isOpen ? t('close') : t('edit')}
                    </Button>
                  )}
                  {!row.isDefault && !isReadOnly && row.hasContent && (
                    <Button
                      data-testid={`make-default-${row.language}`}
                      size="small"
                      onClick={() => setPendingDefault(row.language)}
                    >
                      {t('makeDefaultLanguage', {
                        defaultValue: 'Make default',
                      })}
                    </Button>
                  )}
                  {!row.hasContent && !isReadOnly && (
                    <Button
                      data-testid={`add-language-${row.language}`}
                      size="small"
                      onClick={() => handleAddLanguage(row.language)}
                    >
                      {t('add')}
                    </Button>
                  )}
                  {!row.isDefault && row.hasContent && !isReadOnly && (
                    <Button
                      data-testid={`remove-language-${row.language}`}
                      size="small"
                      type="link"
                      onClick={() => handleRemoveLanguage(row.language)}
                    >
                      {t('remove')}
                    </Button>
                  )}
                </Cluster>
              </Cluster>

              {isOpen && (
                <Stack>
                  <FormFieldWrapper label={t('subject')} required>
                    <Input
                      data-testid={`subject-${row.language}`}
                      value={email.subject}
                      onChange={handleFieldChange(row.language, 'subject')}
                      disabled={isReadOnly}
                    />
                  </FormFieldWrapper>
                  <FormFieldWrapper label={t('htmlContent')} required>
                    <textarea
                      data-testid={`html-${row.language}`}
                      className="w-full border border-divider rounded-md p-2 focus:outline-none focus:ring font-mono"
                      rows={8}
                      value={email.htmlContent}
                      onChange={handleFieldChange(row.language, 'htmlContent')}
                      disabled={isReadOnly}
                    />
                  </FormFieldWrapper>
                  {/* Only pep_signup and pep_login deliver `content`, as the
                      SMS body. Offering the field elsewhere invites copy that
                      is stored, counted and never sent. */}
                  {contentIsSent && (
                    <Stack space={1}>
                      <FormFieldWrapper
                        label={t('smsText', { defaultValue: 'SMS text' })}
                        extra={t('contentIsSmsHint')}
                      >
                        <textarea
                          data-testid={`sms-${row.language}`}
                          className="w-full border border-divider rounded-md p-2 focus:outline-none focus:ring"
                          rows={3}
                          value={email.content}
                          onChange={handleFieldChange(row.language, 'content')}
                          disabled={isReadOnly}
                        />
                      </FormFieldWrapper>
                      {/* Counted at authoring time because nothing downstream
                          does: both SMS providers hand the body straight to
                          the vendor, and an encoding problem surfaces only as
                          a rejected send. A single non-GSM character
                          re-encodes the whole message and drops the limit
                          from 160 to 70. */}
                      {/* Stack, not Cluster: like Text, Cluster destructures
                          its known props and drops data-testid. */}
                      <Stack data-testid={`sms-segments-${row.language}`}>
                        <Cluster space={1}>
                          {(() => {
                            const { encoding, units, segments, limit } =
                              smsSegments(email.content, {
                                shortenerUrl: settings.public.shortenerUrl,
                              });
                            const linkLength = shortLinkLength(
                              email.content,
                              settings.public.shortenerUrl,
                            );
                            return (
                              <>
                                <Chip
                                  tone={segments > 1 ? 'warning' : 'neutral'}
                                >
                                  {/* t(), not a literal: user-visible, seven
                                      locales, and invisible to the i18n guard
                                      as a bare string. */}
                                  {t('smsSegmentCount', {
                                    count: segments,
                                    defaultValue: '{{count}} seg',
                                  })}
                                </Chip>
                                <Chip>{encoding}</Chip>
                                <Text size="small" importance="low">
                                  {`${units} / ${limit}`}
                                </Text>
                                {linkLength !== null && (
                                  <Text size="small" importance="low">
                                    {t('smsShortLinkLength', {
                                      name: '{{linkToShorten}}',
                                      length: linkLength,
                                      defaultValue:
                                        '{{name}} counted as {{length}} characters',
                                    })}
                                  </Text>
                                )}
                              </>
                            );
                          })()}
                        </Cluster>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              )}
            </div>
          );
        })}
      </Stack>

      <Cluster justify="flex-end" space={2}>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button
          data-testid="save-template"
          type="primary"
          onClick={handleSubmit}
          disabled={isReadOnly || isSaving || !name || !language}
        >
          {t('save')}
        </Button>
      </Cluster>
    </div>
  );
};

MessageTemplateEditor.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  template: PropTypes.object,
  configuredLanguages: PropTypes.arrayOf(PropTypes.string),
  canUpdate: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

MessageTemplateEditor.defaultProps = {
  template: null,
  configuredLanguages: [],
  canUpdate: false,
};

export default MessageTemplateEditor;
